import React, { useMemo } from 'react';
import type { AdmissionsCitation } from './types';
import {
  inferEntityKindFromToken,
  isIdentifierToken,
  parseCitationKey,
  resolveCitationHref,
} from './citation-routing';
import { formatCitationIdentifierLabel } from './assistant-id-linkify';

type AssistantCitationDetailProps = {
  citation: AdmissionsCitation;
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.6875rem',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  color: '#2F6E78',
};

const valueStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  color: '#0f172a',
  lineHeight: 1.4,
  wordBreak: 'break-word',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
};

const linkStyle: React.CSSProperties = {
  color: '#0f766e',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  fontWeight: 700,
};

const redactValue = (
  value: string,
  applicationCode: string | null,
  href: string | null
): React.ReactNode => {
  const label = formatCitationIdentifierLabel(value, {
    applicationCode,
    linked: Boolean(href),
  });
  if (href) {
    return <a href={href} style={linkStyle}>{label}</a>;
  }
  return label;
};

const stringifyValue = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export const AssistantCitationDetail = ({ citation }: AssistantCitationDetailProps) => {
  const key = useMemo(() => parseCitationKey(citation), [citation]);
  const applicationHref = resolveCitationHref(citation, 'application');
  const familyHref = resolveCitationHref(citation, 'family');
  const studentHref = resolveCitationHref(citation, 'student');
  const resourceHref = resolveCitationHref(citation, 'resource');
  const applicationCode = typeof citation.applicationCode === 'string' ? citation.applicationCode : null;

  const keyRows = useMemo(() => (
    Object.entries(key)
      .map(([name, value]) => [name, stringifyValue(value)] as const)
      .filter(([, value]) => Boolean(value))
      .slice(0, 6)
  ), [key]);
  const quickLinks = useMemo(() => {
    const items = [
      applicationHref ? { href: applicationHref, label: 'Open application' } : null,
      familyHref ? { href: familyHref, label: 'Open family' } : null,
      studentHref ? { href: studentHref, label: 'Open student' } : null,
      resourceHref ? { href: resourceHref, label: 'Open resource' } : null,
    ].filter((item): item is { href: string; label: string } => item !== null);
    const byHref = new Map<string, { href: string; label: string }>();
    for (const item of items) {
      if (!byHref.has(item.href)) {
        byHref.set(item.href, item);
      }
    }
    return [...byHref.values()];
  }, [applicationHref, familyHref, resourceHref, studentHref]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {quickLinks.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {quickLinks.map((item) => (
            <a
              key={item.href}
              href={item.href}
              style={{
                ...linkStyle,
                display: 'inline-flex',
                alignItems: 'center',
                border: '1px solid rgba(15, 118, 110, 0.25)',
                borderRadius: '999px',
                padding: '4px 10px',
                backgroundColor: 'rgba(15, 118, 110, 0.08)',
                textDecoration: 'none',
              }}
            >
              {item.label}
            </a>
          ))}
        </div>
      ) : null}
      <div style={rowStyle}>
        <span style={labelStyle}>Table</span>
        <span style={valueStyle}>{String(citation.table ?? 'Unknown')}</span>
      </div>
      {citation.module ? (
        <div style={rowStyle}>
          <span style={labelStyle}>Module</span>
          <span style={valueStyle}>{citation.module}</span>
        </div>
      ) : null}
      <div style={rowStyle}>
        <span style={labelStyle}>Field</span>
        <span style={valueStyle}>{citation.fieldPath}</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Preview</span>
        <span style={valueStyle}>{citation.preview || 'No preview available.'}</span>
      </div>
      {applicationCode ? (
        <div style={rowStyle}>
          <span style={labelStyle}>Application</span>
          <span style={valueStyle}>{redactValue(applicationCode, applicationCode, applicationHref)}</span>
        </div>
      ) : null}
      {citation.entityCode ? (
        <div style={rowStyle}>
          <span style={labelStyle}>Entity</span>
          <span style={valueStyle}>
            {(() => {
              const entityKind = inferEntityKindFromToken(citation.entityCode);
              const href = entityKind === 'family'
                ? familyHref
                : entityKind === 'student'
                  ? studentHref
                  : entityKind === 'resource'
                    ? resourceHref
                    : applicationHref;
              return redactValue(citation.entityCode, applicationCode, href);
            })()}
          </span>
        </div>
      ) : null}
      {keyRows.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={labelStyle}>Key</span>
          {keyRows.map(([name, rawValue]) => {
            const tokenKind = inferEntityKindFromToken(rawValue);
            const href = tokenKind === 'family'
              ? familyHref
              : tokenKind === 'student'
                ? studentHref
                : tokenKind === 'resource'
                  ? resourceHref
                  : applicationHref;
            return (
              <div key={name} style={rowStyle}>
                <span style={{ ...labelStyle, letterSpacing: '0.08em' }}>{name}</span>
                <span style={valueStyle}>
                  {isIdentifierToken(rawValue)
                    ? redactValue(rawValue, applicationCode, href)
                    : rawValue}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
